import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

/**
 * Global toast notification component rendered via portal.
 * Fixed position bottom-right, auto-dismisses after configurable duration.
 * Supports success, error, info, and warning types with slide-in/fade-out animation.
 *
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {string} [props.type='info'] - Toast type: 'success', 'error', 'info', 'warning'
 * @param {number} [props.duration=4000] - Auto-dismiss duration in ms (0 to disable)
 * @param {Function} props.onClose - Callback when toast is dismissed
 * @returns {JSX.Element|null} Portal-rendered toast or null when not visible
 *
 * @example
 * <Toast message="Saved!" type="success" onClose={() => setToast(null)} />
 */
function Toast({ message, type = 'info', duration, onClose }) {
  const [exiting, setExiting] = useState(false);

  // Standardized timing: errors require manual dismiss, success/info/warning auto-dismiss
  const effectiveDuration = duration !== undefined ? duration : (type === 'error' ? 0 : 5000);

  useEffect(() => {
    if (!message || effectiveDuration === 0) return;

    const timer = setTimeout(() => {
      setExiting(true);
    }, effectiveDuration);

    return () => clearTimeout(timer);
  }, [message, effectiveDuration]);

  useEffect(() => {
    if (!exiting) return;

    const timer = setTimeout(() => {
      onClose();
    }, 300);

    return () => clearTimeout(timer);
  }, [exiting, onClose]);

  if (!message) return null;

  const icons = {
    success: '\u2713',
    error: '\u2717',
    warning: '\u26A0',
    info: '\u2139'
  };

  const toast = (
    <div className="toast-container" role="status" aria-live="polite">
      <div className={`toast toast--${type} ${exiting ? 'toast--exit' : 'toast--enter'}`}>
        <span className="toast__icon">{icons[type] || icons.info}</span>
        <span className="toast__message">{message}</span>
        <button
          className="toast__close"
          onClick={() => { setExiting(true); }}
          aria-label="Dismiss notification"
        >
          &times;
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(toast, document.body);
}

Toast.propTypes = {
  /** The message to display */
  message: PropTypes.string,
  /** Toast type: 'success', 'error', 'info', 'warning' */
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  /** Auto-dismiss duration in ms (0 to disable) */
  duration: PropTypes.number,
  /** Callback when toast is dismissed */
  onClose: PropTypes.func.isRequired,
};

export default Toast;
